const SECTION_KEYS = {
  Overview: ['legal_name', 'jurisdiction'],
  Registration: ['registered_agent', 'address'],
  Address: ['registered_office']
}

// A section is addressed by a STABLE English key (the rail's `aria-current`
// match, `root.contentSection` and the `data-content-section` scroll anchor all
// compare against it) and LABELLED by a bundle key. The two must not be the
// same string: a translated anchor would break the rail on a language switch.
const SECTION_LABELS = {
  Overview: 'workspace.company.section.overview',
  Registration: 'workspace.company.section.registration',
  Address: 'workspace.company.section.address',
  Resources: 'workspace.company.section.resources'
}

// EVERY value the handlers below need lives on the element's own `scope`,
// and every handler reads it through `el.scope.*`. A closure does not
// survive frank's serialization: the two handlers used to capture `key`,
// `SECTION_LABELS` and `SECTION_KEYS` from this factory, and the scope
// declared the captured value under a DIFFERENT name (`label: key`). The
// rewriter turns each free `key` read into `<ref>.scope.key`, which was
// undefined, so a published shell resolved `SECTION_KEYS[undefined] || []`
// and rendered Overview, Registration and Address as a heading with zero
// rows. Local bypass mode, where the closure still works, looked correct.
// frank-audit flagged the old shape here: "factory captures [key] inside
// returned-object handlers — move to scope:{} on the returned object".
// Both lookups are one-shot per section, so they resolve at factory time
// and travel as plain scope values (FA204). `label` had no reader and is
// gone. `__tests__/companySectionScope.test.js` revives these handlers in a
// fresh realm, which is the only way to reproduce the published failure.
const _section = (key) => ({
  scope: {
    key,
    labelKey: SECTION_LABELS[key] || key,
    rowKeys: SECTION_KEYS[key] || []
  },

  attr: { 'data-content-section': key },
  flow: 'y',
  gap: 'A',
  padding: 'D',
  round: 'radiusSheet',
  background: 'overlay',

  CiTitle: {
    tag: 'h2',
    margin: '0 0 B 0',
    fontSize: 'C',
    fontWeight: '700',
    color: 'title',
    letterSpacing: '-0.01em',
    text: (el) => el.call('polyglot', el.scope.labelKey)
  },

  Rows: {
    flow: 'y',
    childExtends: 'CompanyInfoRow',
    childrenAs: 'state',
    children: (el, s) => {
      const wanted = el.scope.rowKeys || []
      return (s.root.companyInfo || []).filter((r) => wanted.includes(r.key))
    }
  }
})

const RESOURCES = [
  {
    label: 'workspace.company.res.pitchDeck',
    url: 'https://docs.google.com/presentation/d/1oarPeQXg-s1uxXYLBQ8pdtq0V8Y-PXlXjYUwB_n5SZY/edit',
    icon: 'document'
  },
  { label: 'workspace.company.res.landing', url: 'https://dev.symbols.app', icon: 'globe' },
  {
    label: 'workspace.company.res.platformDocs',
    url: 'https://dev.symbols.app/docs/intro',
    icon: 'library'
  },
  {
    label: 'workspace.company.res.sdkDocs',
    url: 'https://docs.google.com/document/d/13IAfzS9eUjapnr5PVK6AVZPkRtjTYG9An7P9GMgRrM8/edit',
    icon: 'code'
  },
  {
    label: 'workspace.company.res.figma',
    url: 'https://www.figma.com/file/LDFnKQSxUxZeeLHt2OT67r?node-id=0:1&locale=en&type=design',
    icon: 'eye'
  },
  { label: 'workspace.company.res.github', url: 'https://github.com/symbo-ls', icon: 'github' },
  { label: 'workspace.company.res.npm', url: 'https://www.npmjs.com/org/symbo.ls', icon: 'code' }
]

export const company = {
  extends: 'ContentPage',
  metadata: (el) => ({ title: el.call('pageTitle', 'workspace.company.page.title') }),
  // `eyebrow` is a CUSTOM prop, so a function value is auto-invoked (Rule 70)
  // and ContentPage's `el.parent.parent.parent.parent.eyebrow` reads the
  // resolved string. `title` is NOT — it is a global HTML attribute read as
  // CONTENT, the one case with no working rewrite form
  // (docs/I18N-CONVENTION.md §1.2) — so this page overrides ContentPage's
  // Title slot directly (below) instead of translating the `title` prop.
  // Same contract as pages/my.js and packages/shared/pages/profile.js.
  eyebrow: (el) => el.call('polyglot', 'workspace.company.page.eyebrow'),
  title: 'Company',
  // `sectionKey` is the SCROLL KEY: ContentPageSectionLink matches it against
  // the `[data-content-section="<key>"]` attribute `_section` writes above and
  // publishes it to `root.contentSection`. It stays the English string those
  // attributes already carry and must NEVER be translated — a translated key
  // breaks the scroll on the first switch. `label` is the VISIBLE string and
  // resolves here, inside ContentPage's reactive `children` factory, so the
  // rail re-renders on a language switch.
  sections: (el) => [
    { sectionKey: 'Overview', label: el.call('polyglot', 'workspace.company.section.overview') },
    {
      sectionKey: 'Registration',
      label: el.call('polyglot', 'workspace.company.section.registration')
    },
    { sectionKey: 'Address', label: el.call('polyglot', 'workspace.company.section.address') },
    { sectionKey: 'Resources', label: el.call('polyglot', 'workspace.company.section.resources') }
  ],

  // Content mounts in ContentPage's main column (PageMain.Body) — the
  // notes-page skeleton; see components/ContentPage.js.
  PageMain: {
    // ContentPage reads the page's `title` prop into this slot. A `title:`
    // prop cannot carry a translation (§1.2), so the page overrides the slot
    // itself — a plain `text:` on a LEAF element, which takes the TEMPLATE
    // form and re-resolves on a language switch.
    Header: {
      LeftGroup: {
        // One title per host (design-direction.md §3): "Company" is the pane
        // bar word for word, so it yields under one. The eyebrow stays.
        Title: {
          hide: (el, s) => el.call('paneTitleBarShown', s),
          text: '{{ workspace.company.page.heading | polyglot }}'
        }
      }
    },
    Body: {
      PageBody: {
        flow: 'y',
        gap: 'C',
        flex: '1',
        minWidth: '0',
        maxWidth: 'I',

        OverviewCard: _section('Overview'),

        RegistrationCard: _section('Registration'),
        AddressCard: _section('Address'),

        ResourcesCard: {
          ..._section('Resources'),
          Rows: null,

          Links: {
            flow: 'y',
            childrenAs: 'state',
            children: () => RESOURCES,
            childExtends: {
              extends: 'Link',
              href: (el, s) => s.url,
              target: '_blank',
              textDecoration: 'none',
              flow: 'x',
              align: 'center space-between',
              gap: 'B',
              padding: 'A 0',
              ':hover': { background: 'hover' },

              Left: {
                flow: 'x',
                align: 'center left',
                gap: 'A',
                Icon: {
                  icon: (el, s) => s.icon || 'link',
                  boxSize: 'A1',
                  color: 'accentInk'
                },
                Label: {
                  tag: 'span',
                  fontSize: 'B',
                  fontWeight: '700',
                  color: 'title',
                  // KEY-IN-STATE — the RESOURCES rows above carry bundle keys.
                  text: (el, s) => el.call('polyglot', s.label || '')
                }
              },
              Arrow: {
                tag: 'span',
                fontSize: 'Z2',
                color: 'caption',
                text: '\u2192'
              }
            }
          }
        }
      }
    }
  }
}
